import React, {Fragment} from 'react';
import PropTypes from 'prop-types';
import {isEmpty} from 'lodash';
import bowser from 'bowser';
import classNames from 'classnames';
import {REFRESH__16} from '@homeaway/svg-defs/lib/defs/REFRESH__16';
import {SVGIcon} from '@homeaway/react-svg';
import Experiment from '@homeaway/react-experiment';

import {FallbackUI} from '../../molecules/FallbackUI/FallbackUI';

import translate from '../../../utils/translations';
import {Message} from '../Message/Message';
import {MessageForm} from '../MessageForm/MessageForm';
import {MessagesContact} from '../MessagesContact/MessagesContact';
import {PropertyContact} from '../PropertyContact/PropertyContact';
import {ServiceFeeSummary} from '../ServiceFeeSummary/ServiceFeeSummary';
import {TripSummary} from '../TripSummary/TripSummary';
import GuestsOfGuest from '../../organisms/GuestsOfGuest/GuestsOfGuest';
import {TripSummaryCard} from '../TripSummaryCard/TripSummaryCard';
import {ProtectPaymentAlert} from '../ProtectPaymentAlert/ProtectPaymentAlert';

import './ConversationMessages.less';
import {
    TRIP_MESSAGES,
    tripMessageSetPresented,
} from '../../../utils/EDAP/ConversationMessages';
import {MessagesContext} from '../../context/MessagesContext';
import {StayConsumer} from '../../context/StayContext';

import {VRBO_LOGO} from '../../../utils/appConfigData';

import {withErrorBoundary} from '../../ErrorBoundary/ErrorBoundary';

export class ConversationMessages extends React.Component {
    static propTypes = {
        adults: PropTypes.number,
        allowsReplies: PropTypes.bool,
        arrival: PropTypes.string,
        backUrl: PropTypes.string,
        contact: PropTypes.shape({
            memberSince: PropTypes.string,
            name: PropTypes.string,
            ownerProfilePhoto: PropTypes.string,
        }),
        children: PropTypes.number,
        conversationId: PropTypes.string,
        departure: PropTypes.string,
        expirationDateTime: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        fullPhoneNumber: PropTypes.string,
        isHybridInbox: PropTypes.bool,
        isPrimaryGuest: PropTypes.bool,
        messages: PropTypes.arrayOf(
            PropTypes.shape({
                actions: PropTypes.arrayOf(
                    PropTypes.shape({
                        actionTitle: PropTypes.string,
                        actionUrl: PropTypes.string,
                    })
                ),
                attachments: PropTypes.arrayOf(
                    PropTypes.shape({
                        attachmentId: PropTypes.string,
                        downloadUrl: PropTypes.string,
                        fileName: PropTypes.string,
                    })
                ),
                avatar: PropTypes.string,
                displayName: PropTypes.string,
                owner: PropTypes.string,
                role: PropTypes.string,
                sentText: PropTypes.string,
                text: PropTypes.string,
                title: PropTypes.string,
            })
        ),
        onRefreshMessages: PropTypes.func,
        priceDetails: PropTypes.shape({}),
        propertyId: PropTypes.number,
        propertyImageC5: PropTypes.string,
        propertyUrl: PropTypes.string,
        quoteTotals: PropTypes.arrayOf(PropTypes.shape({})),
        reservationUuid: PropTypes.string,
        responseTime: PropTypes.string,
        serviceFeeSummary: PropTypes.shape({}),
        systemData: PropTypes.shape({}),
        translations: PropTypes.shape({}),
        tripSummary: PropTypes.shape({
            arrivalDate: PropTypes.string,
            departureDate: PropTypes.string,
        }),
    };

    static defaultProps = {
        backUrl: '/traveler/th/inbox',
        contact: {},
        messages: [],
        onRefreshMessages: () => {},
        systemData: {},
        translations: {},
        tripSummary: {},
        loading: false,
        data: {},
    };

    constructor(props) {
        super(props);
        this.messageWindow = React.createRef();
        this.messagesList = React.createRef();
    }

    componentDidMount() {
        // TODO: use https://reactjs.org/docs/react-component.html#getsnapshotbeforeupdate instead of setTimeout
        setTimeout(() => {
            // scroll message form up to get over safari bar
            if (bowser.iphone && bowser.safari) {
                window.scrollTo(0, 110);
            }
        }, 250);
        tripMessageSetPresented({
            actionLocation: TRIP_MESSAGES,
            reservationUUID: this.props.reservationUuid,
        });
        this.scrollToBottomOfMessagesOnMobile();
    }

    static contextType = MessagesContext;

    onSendDone = (response) => {
        const {
            store: {messages},
            actions: {setConversationMessages},
        } = this.messagesContext;
        messages.push(response.message);
        setConversationMessages(messages);
        this.scrollToBottomOfMessagesOnMobile();
    };

    onDeleteAttachment = (msg, updateAttachment) => {
        const {
            store: {messages},
            actions: {setConversationMessages},
        } = this.messagesContext;
        const messageIndex = messages.findIndex(
            (message) => message.id === msg.id
        );
        if (messageIndex > -1) {
            // If message was without text and the only attachment got removed
            if (!messages[messageIndex].text && updateAttachment.length === 0) {
                messages.splice(messageIndex, 1);
            } else {
                messages[messageIndex].attachments = updateAttachment;
            }
        }
        setConversationMessages(messages);
    };

    scrollToBottomOfMessagesOnMobile = () => {
        // scroll to bottom of the messages on xs
        if (this.messagesList.current) {
            this.messageWindow.current.scrollTop =
                this.messagesList.current.clientHeight;
        }
    };

    get messagesContext() {
        if (isEmpty(this.context)) {
            throw new Error(
                'messagesContext must be used within a MessagesContextProvider'
            );
        }
        return this.context;
    }

    render() {
        const {
            allowsReplies,
            arrival,
            backUrl,
            contact,
            conversationId,
            countdownSummary,
            departure,
            expirationDateTime,
            fullPhoneNumber,
            handelBannerClick,
            isHybridInbox,
            isPrimaryGuest,
            onRefreshMessages,
            ownerAvatar,
            priceDetails,
            propertyImageC5,
            reservationUuid,
            responseTime,
            serviceFeeSummary,
            systemData,
            translations,
            tripSummary,
        } = this.props;

        const arrivalDate = tripSummary.arrivalDate || arrival;
        const departureDate = tripSummary.departureDate || departure;
        const {messages} = this.messagesContext.store;
        const prices = priceDetails || {};
        const ERROR_BOUNDARY_TEXT = translate(
            translations,
            'trav-dash/trav-home',
            'messages',
            'error.boundary.message'
        );

        const ERROR_BOUNDARY_TITLE = translate(
            translations,
            'trav-dash/trav-home',
            'messages',
            'th.inbox.conversation.buttons.messages'
        );
        let msgs = [];
        if (messages && messages.length > 0) {
            msgs = messages.map((msg, index) => {
                if (!msg) {
                    return null;
                }
                let avatarKey = msg.avatar;
                if (systemData?.theme === 'vrbo' && msg.role === 'SYSTEM') {
                    avatarKey = VRBO_LOGO;
                }
                return (
                    <StayConsumer>
                        {({stay}) => {
                            const {ctaDetails} = stay;
                            return (
                                <Message
                                    actions={msg.actions}
                                    attachments={msg.attachments}
                                    attributes={msg.attributes}
                                    avatar={avatarKey}
                                    backUrl={backUrl}
                                    conversationId={conversationId}
                                    ctaDetails={ctaDetails}
                                    displayName={msg.displayName}
                                    isHybridInbox={isHybridInbox}
                                    key={index}
                                    role={msg.role}
                                    sentText={msg.sentText}
                                    text={msg.text}
                                    title={msg.title}
                                    translations={translations}
                                    type={msg.type}
                                    onDeleteComplete={(updatedAttachments) =>
                                        this.onDeleteAttachment(
                                            msg,
                                            updatedAttachments
                                        )
                                    }
                                />
                            );
                        }}
                    </StayConsumer>
                );
            });
        }

        const rightRailClassNames = classNames(
            'col-xs-12',
            'col-sm-5',
            'col-md-4',
            'pull-right',
            'ConversationMessages__col--right',
            'hidden-xs',
            'ConversationMessages__col--rightRailReorder'
        );

        return (
            <div className="ConversationMessages row">
                <div className={rightRailClassNames}>
                    {!isEmpty(contact) && systemData.isTablet && (
                        <PropertyContact
                            avatar={contact.ownerProfilePhoto}
                            backUrl={backUrl}
                            conversationId={conversationId}
                            memberSince={contact.memberSince}
                            ownerName={contact.name}
                            phone={fullPhoneNumber}
                            responseTime={responseTime}
                            translations={translations}
                        />
                    )}

                    <TripSummary
                        actionLocation={TRIP_MESSAGES}
                        backUrl={backUrl}
                        expirationDateTime={expirationDateTime}
                        isHybridInbox={isHybridInbox}
                        translations={translations}
                    />

                    <Experiment name={'GoG_removal_Vrbex'} variant={'off'}>
                        {!isHybridInbox &&
                            withErrorBoundary(
                                <GuestsOfGuest
                                    actionLocation={TRIP_MESSAGES}
                                    conversationId={conversationId}
                                    isPrimary={isPrimaryGuest}
                                    reservationUuid={reservationUuid}
                                    translations={translations}
                                />,
                                'guests-of-guest'
                            )}
                    </Experiment>
                    {serviceFeeSummary && (
                        <ServiceFeeSummary
                            checkoutUrl={prices.checkoutUrl}
                            serviceFeeSummary={serviceFeeSummary}
                        />
                    )}
                </div>

                <div className="col-xs-12 col-sm-7 col-md-8 ConversationMessages__col--left">
                    {allowsReplies && (
                        <Fragment>
                            {systemData.isDesktop && (
                                <TripSummaryCard
                                    actionLocation={TRIP_MESSAGES}
                                    arrivalDate={arrivalDate}
                                    backUrl={backUrl}
                                    contact={contact}
                                    conversationId={conversationId}
                                    departureDate={departureDate}
                                    expirationDateTime={
                                        countdownSummary.expirationDateTime
                                    }
                                    fullPhoneNumber={fullPhoneNumber}
                                    handelBannerClick={handelBannerClick}
                                    ownerAvatar={ownerAvatar}
                                    propertyImageC5={propertyImageC5}
                                    propertyUrl={this.props.propertyUrl}
                                    reservationUuid={reservationUuid}
                                    responseTime={responseTime}
                                    translations={translations}
                                />
                            )}
                            <MessageForm
                                conversationId={conversationId}
                                onSendDone={this.onSendDone}
                                placeholder={translate(
                                    translations,
                                    'trav-dash/trav-home',
                                    'messages',
                                    'th.inbox.conversation.buttons.sendLong',
                                    {
                                        first_name:
                                            contact.name ||
                                            translate(
                                                translations,
                                                'trav-dash/trav-home',
                                                'messages',
                                                'th.inbox.conversation.message.host'
                                            ),
                                    }
                                )}
                                systemData={systemData}
                                translations={translations}
                            />
                        </Fragment>
                    )}
                    {/* TODO- Move messages list to a new component JIRA: SE-5634 */}
                    {withErrorBoundary(
                        <div
                            ref={this.messageWindow}
                            className="ConversationMessages__messages"
                        >
                            <div className="ConversationMessages__messages__header hidden-xs">
                                <h3>
                                    {translate(
                                        translations,
                                        'trav-dash/trav-home',
                                        'messages',
                                        'th.inbox.conversation.buttons.messages'
                                    )}
                                </h3>
                                <a href="#" onClick={onRefreshMessages}>
                                    <span className="ConversationMessages__messages__text">
                                        {translate(
                                            translations,
                                            'trav-dash/trav-home',
                                            'messages',
                                            'th.inbox.conversation.refresh'
                                        )}
                                    </span>
                                    <SVGIcon markup={REFRESH__16} useFill />
                                </a>
                            </div>

                            <div>
                                {!isEmpty(contact) && (
                                    <MessagesContact
                                        avatar={contact.ownerProfilePhoto}
                                        memberSince={contact.memberSince}
                                        ownerName={contact.name}
                                        translations={translations}
                                    />
                                )}
                                {systemData.isMobile && (
                                    <StayConsumer>
                                        {({stay}) => {
                                            const {
                                                unit: {
                                                    integratedPropertyManager,
                                                },
                                            } = stay;
                                            return (
                                                !integratedPropertyManager && (
                                                    <div className="ConversationMessages__messages__payment-alert">
                                                        <ProtectPaymentAlert
                                                            translations={
                                                                translations
                                                            }
                                                        />
                                                    </div>
                                                )
                                            );
                                        }}
                                    </StayConsumer>
                                )}

                                <div className="ConversationMessages__messages__heading--xs text-center visible-xs">
                                    <em className="small">
                                        {translate(
                                            translations,
                                            'trav-dash/trav-home',
                                            'messages',
                                            'th.inbox.conversation.beginning',
                                            {NAME: contact.name}
                                        )}
                                    </em>
                                </div>
                                {msgs && msgs.length > 0 && (
                                    <div
                                        ref={this.messagesList}
                                        className="ConversationMessages__messages__list"
                                    >
                                        {msgs}
                                    </div>
                                )}
                            </div>
                        </div>,
                        'messages-list',
                        <FallbackUI
                            errorText={ERROR_BOUNDARY_TEXT}
                            title={ERROR_BOUNDARY_TITLE}
                        />
                    )}
                </div>
            </div>
        );
    }
}
